import { useEffect, useState } from "react";
import { api, fmtIST } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common";
import { useCoachingCitations } from "@/components/ConsultantPerformanceReport";
import { CoachingDocumentView, coachingPeriodLabel } from "@/components/CoachingDocumentView";
import { GraduationCap } from "lucide-react";

const PERIOD_OPTIONS = [["month", "This month"], ["week", "This week"]];

export default function MyCoachingPage() {
  const [periodType, setPeriodType] = useState("month");
  const [doc, setDoc] = useState(undefined);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setDoc(undefined);
    api.get(`/ai/coaching/me?period_type=${periodType}`).then((r) => setDoc(r.data)).catch(() => setDoc(null));
    api.get(`/ai/coaching/me/history?period_type=${periodType}`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [periodType]);

  const { byEvidenceId } = useCoachingCitations(doc?.coaching_id);

  return (
    <div className="space-y-4" data-testid="my-coaching-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <GraduationCap className="h-6 w-6" />My Coaching
          </h1>
          <p className="text-sm text-muted-foreground">
            Personalized, evidence-based coaching on your own pre-Signup sales work — never a
            comparison with anyone else. A manager reviews every generation before you see it.
          </p>
        </div>
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger data-testid="my-coaching-period-filter" className="h-8 w-40 text-xs ml-auto"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIOD_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {doc === undefined ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : !doc ? (
        <EmptyState icon={GraduationCap} title="No coaching available yet for this period"
          hint="Coaching is generated monthly (or on request by your manager) and only appears here once a manager reviews and releases it." />
      ) : doc.content_status === "legacy_corrupted" ? (
        <Card data-testid="my-coaching-corrupted"><CardContent className="pt-4">
          <p className="text-sm text-muted-foreground italic">
            This older AI content cannot be displayed safely. Regenerate it to create a verified version.
          </p>
        </CardContent></Card>
      ) : (
        <Card data-testid="my-coaching-current"><CardContent className="pt-4 space-y-2">
          <p className="text-[10px] text-muted-foreground">
            Released {fmtIST(doc.decided_at || doc.generated_at)} · Advisory only — always use your own judgment.
          </p>
          <CoachingDocumentView doc={doc} byEvidenceId={byEvidenceId} showManagementNotes={false} />
        </CardContent></Card>
      )}

      {history.length > 1 && (
        <Card data-testid="my-coaching-history"><CardContent className="pt-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Past periods</p>
          <div className="space-y-1.5">
            {history.map((h) => (
              <div key={h.coaching_id} className="text-xs flex flex-wrap items-center gap-x-2 border-l-2 pl-2">
                <span className="text-muted-foreground">{coachingPeriodLabel(h) || fmtIST(h.decided_at || h.generated_at)}</span>
                <span className="break-words">{h.coaching?.executive_summary || h.coaching?.overall_read?.biggest_opportunity}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
